import { useEffect, useState } from 'react';
import { useFilterStore } from '../../stores/filterStore';
import { SelectedFilterOptionTag } from '../atoms/SelectedFilterOptionTag';

interface SelectedFilterTagsProps {
  onChangeCheckedFilter: (filterId: string, optionName: string) => void;
  mode?: 'table' | 'panel';
}

export function SelectedFilterTags({
  onChangeCheckedFilter,
  mode = 'panel',
}: SelectedFilterTagsProps) {
  const { getSelectedOptions } = useFilterStore();
  const [displayMode, setDisplayMode] = useState<'regular' | 'compact'>(
    'regular',
  );

  const displayOptions = getSelectedOptions(
    mode === 'table' ? 'filters' : 'tempFilters',
  );

  useEffect(() => {
    if (mode === 'panel') {
      return;
    }

    const calculateDisplayMode = () => {
      // 태그의 기본 너비 계산 (px-4 + py-2 + 아이콘 16px + gap 4px)
      const BASE_TAG_WIDTH = 32 + 16 + 4; // 52px
      const GAP_WIDTH = 4; // gap-1 = 4px
      const CHAR_WIDTH = 8; // body-14m 기준 한 글자당 약 8px

      // 데스크톱 기준 컨테이너 너비 (최대 px 제한)
      const DESKTOP_CONTAINER_WIDTH = Math.min(750, window.innerWidth - 64);

      // 각 태그의 실제 너비 계산
      const tagWidths = displayOptions.map((option) => {
        const textWidth = option.optionName.length * CHAR_WIDTH;
        return BASE_TAG_WIDTH + textWidth;
      });

      // 한 줄에 들어갈 수 있는 태그들의 총 너비 계산
      const totalWidth = tagWidths.reduce(
        (acc, width) => acc + width + GAP_WIDTH,
        0,
      );

      // 예상되는 행 수 계산 (올림)
      const expectedRows = Math.ceil(totalWidth / DESKTOP_CONTAINER_WIDTH);

      // 2열 이상이 예상되면 compact 모드로 전환
      const shouldBeCompact = expectedRows > 1;

      if (shouldBeCompact !== (displayMode === 'compact')) {
        setDisplayMode(shouldBeCompact ? 'compact' : 'regular');
      }
    };

    calculateDisplayMode();
    window.addEventListener('resize', calculateDisplayMode);

    return () => {
      window.removeEventListener('resize', calculateDisplayMode);
    };
  }, [displayOptions, displayMode, mode]);

  return mode === 'table' ? (
    <div
      className={`flex ${displayMode === 'regular' ? 'flex-nowrap max-h-17' : 'flex-wrap max-h-13'} gap-1 max-w-[750px] overflow-hidden`}
    >
      {displayOptions.map((option) => (
        <SelectedFilterOptionTag
          key={`${option.filterId}-${option.optionName}-${option.selectedAt}`}
          option={option}
          onChangeCheckedFilter={onChangeCheckedFilter}
          compact={displayMode === 'compact'}
        />
      ))}
    </div>
  ) : (
    <div className="flex flex-1 flex-wrap gap-1 h-fit overflow-hidden">
      {displayOptions.map((option) => (
        <SelectedFilterOptionTag
          key={`${option.filterId}-${option.optionName}-${option.selectedAt}`}
          option={option}
          onChangeCheckedFilter={onChangeCheckedFilter}
        />
      ))}
    </div>
  );
}
